const MAX_EDGE = 1600;
const TARGET_BYTES = 700 * 1024;

function guessMime(file: File): string {
  if (file.type) return file.type;
  const name = file.name.toLowerCase();
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".webp")) return "image/webp";
  return "image/jpeg";
}

async function canvasToJpeg(source: CanvasImageSource, width: number, height: number, quality: number): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not compress image");
  ctx.drawImage(source, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", quality);
  });
  if (!blob) throw new Error("Could not compress image");
  return blob;
}

/** Shrink camera photos so KYC JSON uploads stay under the API body limit. */
export async function compressKycImage(file: File): Promise<File> {
  if (!file.type.startsWith("image/") && !guessMime(file).startsWith("image/")) {
    throw new Error("image_type_invalid");
  }

  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    let quality = 0.82;
    let blob = await canvasToJpeg(bitmap, width, height, quality);
    while (blob.size > TARGET_BYTES && quality > 0.5) {
      quality -= 0.12;
      blob = await canvasToJpeg(bitmap, width, height, quality);
    }
    bitmap.close?.();
    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    if (file.size <= TARGET_BYTES) return file;
    throw new Error("image_too_large");
  }
}
